import { createRouter, createWebHistory, type RouteRecordRaw, type RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useTenantStore } from '@/stores/tenant'
import { useNotificationStore } from '@/stores/notifications'
import { useSetupStore } from '@/stores/setup'

const routes: RouteRecordRaw[] = [
  // ── Public / auth ──────────────────────────────────────────────────────────
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { public: true },
  },
  {
    // PND-AI / ADR-084 — public self-service signup. The backend has had these four
    // endpoints since July; this is the half that was missing, so until now the only
    // route onto the platform was Peter creating your account by hand.
    path: '/register',
    name: 'Register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: { public: true },
  },
  {
    // `requiresAuth: false`, not `public: true`: the `public` branch below bounces anyone
    // already signed in to the dashboard, and somebody resetting a password may well have a
    // live session on the machine they are doing it from — that is a common reason to be
    // resetting it.
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { requiresAuth: false },
  },
  {
    // C1.4 — a newly created user lands here from the link in their welcome
    // message. `requiresAuth: false` rather than `public: true`: the `public`
    // branch bounces anyone already signed in to the dashboard, which would stop
    // an operator from opening a link on a machine where they are logged in.
    path: '/set-password',
    name: 'SetPassword',
    component: () => import('@/views/auth/SetPasswordView.vue'),
    meta: { requiresAuth: false },
  },
  {
    // C1.2 — PPPoE self-care. The customer's own page: they arrive here from a link
    // or a printed URL, sign in with the account number on their invoice, and pay.
    //
    // `requiresAuth: false` rather than `public: true`, for the same reason as
    // /set-password above: the `public` branch bounces anyone already signed in to
    // the operator dashboard, which would stop an operator opening a customer's link
    // to see what the customer sees.
    //
    // Path is /self-care/ and not under /portal/: `/portal/*` is proxied to the
    // backend (vite.config.ts, and the captive-portal routes in portal.routes.ts),
    // so a client-side route there would never be reached.
    path: '/self-care/:siteCode',
    name: 'SelfCare',
    component: () => import('@/views/selfcare/SelfCareView.vue'),
    meta: { requiresAuth: false, customerFacing: true },
  },

  // ── App shell ───────────────────────────────────────────────────────────────
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/dashboard' },

      {
        /**
         * The guided first run (ADR-084), and where the gate sends a tenant who clicks
         * something they cannot use yet.
         *
         * `setupExempt` keeps it reachable while gated — without it the guard would redirect
         * this route to itself, which vue-router reports as an infinite redirect and which
         * presents to the user as a blank screen.
         */
        path: 'setup',
        name: 'Setup',
        component: () => import('@/views/setup/SetupView.vue'),
        meta: { requiresTenant: true, breadcrumb: 'Setup', setupExempt: true },
      },

      // ── Dashboard ────────────────────────────────────────────────────────────
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { breadcrumb: 'Dashboard' },
      },

      // ── Sites ────────────────────────────────────────────────────────────────
      {
        path: 'sites',
        name: 'Sites',
        component: () => import('@/views/sites/SiteListView.vue'),
        meta: { roles: ['super_admin', 'site_owner'], breadcrumb: 'Sites' },
      },
      {
        path: 'sites/new',
        name: 'SiteCreate',
        component: () => import('@/views/sites/SiteEditView.vue'),
        meta: { roles: ['super_admin', 'site_owner'], breadcrumb: 'New Site' },
      },
      {
        path: 'sites/:siteId',
        name: 'SiteDetail',
        component: () => import('@/views/sites/SiteDetailView.vue'),
        meta: { roles: ['super_admin', 'site_owner'], breadcrumb: 'Site' },
      },
      {
        path: 'sites/:siteId/edit',
        name: 'SiteEdit',
        component: () => import('@/views/sites/SiteEditView.vue'),
        meta: { roles: ['super_admin', 'site_owner'], breadcrumb: 'Edit Site' },
      },

      // ── Locations ────────────────────────────────────────────────────────────
      {
        path: 'locations',
        name: 'Locations',
        component: () => import('@/views/locations/LocationListView.vue'),
        meta: { roles: ['super_admin', 'site_owner'], breadcrumb: 'Locations' },
      },
      {
        path: 'locations/new',
        name: 'LocationCreate',
        component: () => import('@/views/locations/LocationDetailView.vue'),
        meta: { roles: ['super_admin', 'site_owner'], breadcrumb: 'New Location' },
      },
      {
        path: 'locations/:locationId',
        name: 'LocationDetail',
        component: () => import('@/views/locations/LocationDetailView.vue'),
        meta: { roles: ['super_admin', 'site_owner'], breadcrumb: 'Location' },
      },

      // ── Devices ──────────────────────────────────────────────────────────────
      {
        path: 'devices',
        name: 'Devices',
        component: () => import('@/views/devices/DeviceListView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'technician'], breadcrumb: 'Devices' },
      },
      {
        path: 'devices/new',
        name: 'DeviceCreate',
        component: () => import('@/views/devices/DeviceEditView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'technician'], breadcrumb: 'New Device' },
      },
      {
        path: 'devices/:deviceId',
        name: 'DeviceDetail',
        component: () => import('@/views/devices/DeviceDetailView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'technician'], breadcrumb: 'Device' },
      },
      {
        path: 'devices/:deviceId/edit',
        name: 'DeviceEdit',
        component: () => import('@/views/devices/DeviceEditView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'technician'], breadcrumb: 'Edit Device' },
      },

      // ── Services ─────────────────────────────────────────────────────────────
      {
        path: 'services',
        name: 'Services',
        component: () => import('@/views/services/ServiceListView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Services' },
      },
      {
        path: 'services/stock',
        name: 'VoucherStock',
        component: () => import('@/views/services/StockView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Voucher Stock' },
      },
      {
        path: 'services/:serviceId',
        name: 'ServiceDetail',
        component: () => import('@/views/services/ServiceDetailView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Service' },
      },

      // ── Vouchers ─────────────────────────────────────────────────────────────
      {
        path: 'vouchers/sold',
        name: 'SoldVouchers',
        component: () => import('@/views/vouchers/SoldVouchersView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Sold Vouchers' },
      },
      {
        path: 'vouchers',
        name: 'Vouchers',
        component: () => import('@/views/vouchers/VoucherListView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Vouchers' },
      },
      {
        path: 'vouchers/generate',
        name: 'VoucherGenerate',
        component: () => import('@/views/vouchers/VoucherGenerateView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Generate Vouchers' },
      },
      {
        path: 'vouchers/generate-device',
        name: 'DeviceVoucherGenerate',
        component: () => import('@/views/vouchers/DeviceVoucherGenerateView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Generate on Device' },
      },
      {
        path: 'vouchers/history',
        name: 'VoucherGenerationHistory',
        component: () => import('@/views/vouchers/VoucherGenerationHistoryView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Generation History' },
      },
      {
        path: 'vouchers/imports',
        name: 'VoucherImports',
        component: () => import('@/views/vouchers/VoucherImportsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Voucher Imports' },
      },
      {
        path: 'vouchers/print',
        name: 'VoucherPrint',
        component: () => import('@/views/vouchers/VoucherPrintView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Print Vouchers' },
      },

      // ── Customers (CIP) ──────────────────────────────────────────────────────
      // Static sub-routes MUST come before the dynamic :customerId catch-all.
      {
        path: 'customers',
        name: 'Customers',
        component: () => import('@/views/customers/CustomerListView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'customer_support'], breadcrumb: 'Customers' },
      },
      {
        path: 'customers/analytics',
        name: 'CIPAnalytics',
        component: () => import('@/views/customers/CIPAnalyticsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Customer Analytics' },
      },
      {
        path: 'customers/loyalty',
        name: 'LoyaltySettings',
        component: () => import('@/views/loyalty/LoyaltySettingsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Loyalty & Bonuses' },
      },
      {
        path: 'customers/lucky-spin',
        name: 'LuckySpin',
        component: () => import('@/views/customers/LuckySpinView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Lucky Spin' },
      },
      {
        path: 'customers/campaigns',
        name: 'RewardCampaigns',
        component: () => import('@/views/customers/RewardCampaignsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Reward Campaigns' },
      },
      {
        path: 'customers/churn',
        name: 'ChurnRisk',
        component: () => import('@/views/customers/ChurnRiskView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Churn Risk' },
      },
      {
        path: 'referral',
        name: 'ReferralEngine',
        component: () => import('@/views/referral/ReferralEngineView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Referral Engine' },
      },
      {
        path: 'customers/:customerId',
        name: 'CustomerProfile',
        component: () => import('@/views/customers/CustomerProfileView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'customer_support'], breadcrumb: 'Customer' },
      },

      // ── Advertising ──────────────────────────────────────────────────────────
      {
        path: 'ads',
        name: 'AdLibrary',
        component: () => import('@/views/ads/AdLibraryView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Ad Library' },
      },
      {
        path: 'ads/analytics',
        name: 'AdAnalytics',
        component: () => import('@/views/ads/AdAnalyticsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Ad Analytics' },
      },
      {
        path: 'ads/watch-to-surf',
        name: 'WatchToSurf',
        component: () => import('@/views/ads/WatchToSurfView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Watch to Surf' },
      },
      {
        path: 'ads/surf-sessions',
        name: 'AdSurfSessions',
        component: () => import('@/views/ads/AdSurfSessionsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Surf Sessions' },
      },
      {
        path: 'ads/marketplace',
        name: 'AdMarketplace',
        component: () => import('@/views/ads/AdMarketplaceView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Advert Marketplace' },
      },
      {
        path: 'ads/advertisers',
        name: 'Advertisers',
        component: () => import('@/views/ads/AdvertiserListView.vue'),
        meta: { requiresTenant: false, roles: ['super_admin', 'site_owner'], breadcrumb: 'Advertisers' },
      },
      {
        path: 'ads/advertisers/:id',
        name: 'AdvertiserDashboard',
        component: () => import('@/views/ads/AdvertiserDashboardView.vue'),
        meta: { requiresTenant: false, roles: ['super_admin', 'site_owner'], breadcrumb: 'Advertiser Dashboard' },
      },

      // ── Hotspot broadcasts (ADR-069) ──────────────────────────────────────────
      // The owner's own view. A separate route rather than a variant of the NOC dashboard:
      // they answer different questions, and merging them would compromise both.
      {
        path: 'my-site',
        name: 'OwnerDashboard',
        component: () => import('@/views/OwnerDashboardView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'My Site' },
      },
      {
        path: 'broadcasts',
        name: 'Broadcasts',
        component: () => import('@/views/hotspot/BroadcastsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Broadcasts' },
      },

      // ── Portal (ADR-036 multi-portal) ─────────────────────────────────────────
      {
        path: 'portals',
        name: 'Portals',
        component: () => import('@/views/portal/PortalListView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Captive Portals' },
      },
      {
        path: 'portals/:id',
        name: 'PortalEditNew',
        component: () => import('@/views/portal/PortalEditorView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Edit Portal' },
      },
      {
        path: 'portal-editor',
        name: 'PortalEditor',
        component: () => import('@/views/portal/PortalLegacyEditorView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Portal Editor' },
      },
      {
        path: 'portal-preview',
        name: 'PortalPreview',
        component: () => import('@/views/portal/PortalPreviewView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Portal Preview' },
      },

      // ── Billing ──────────────────────────────────────────────────────────────
      {
        path: 'billing/payment-status/:id',
        name: 'PaymentStatus',
        component: () => import('@/views/billing/PaymentStatusView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Payment Status' },
      },
      {
        path: 'billing/site-commission',
        name: 'SiteCommission',
        component: () => import('@/views/billing/SiteCommissionView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Site Commission' },
      },
      {
        path: 'transactions',
        name: 'Transactions',
        component: () => import('@/views/billing/TransactionListView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Transactions' },
      },
      {
        path: 'transactions/audits',
        name: 'TransactionAudits',
        component: () => import('@/views/billing/TransactionAuditsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Transaction Audits' },
      },
      {
        path: 'payments/providers',
        name: 'PaymentProviders',
        component: () => import('@/views/billing/SitePaymentProvidersView.vue'),
        // super_admin only — this screen reads and writes gateway credentials, which are
        // platform infrastructure rather than a site's own settings (2026-08-27).
        meta: { requiresTenant: true, roles: ['super_admin'], breadcrumb: 'Payment Providers' },
      },
      {
        path: 'wallet',
        name: 'Wallet',
        component: () => import('@/views/billing/WalletView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Wallet' },
      },
      {
        path: 'withdrawals',
        name: 'WithdrawalApprovals',
        component: () => import('@/views/billing/WithdrawalApprovalsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner'], breadcrumb: 'Withdrawal Approvals' },
      },
      {
        /**
         * The page the setup checklist has always linked to and which never existed, so the
         * one step telling an owner to go and load credits sent them to a 404. Site-facing on
         * purpose: /admin/sms-credits is super_admin and does the opposite job (buying INTO
         * the platform pool rather than out of it).
         */
        path: 'billing/sms-credits',
        name: 'SiteSmsCredits',
        component: () => import('@/views/billing/SmsCreditsSiteView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'SMS Credits' },
      },
      {
        path: 'billing/ledger',
        name: 'Ledger',
        component: () => import('@/views/billing/LedgerView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Ledger' },
      },
      {
        path: 'expenses',
        name: 'Expenses',
        component: () => import('@/views/expenses/ExpensesView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Expenses' },
      },
      {
        path: 'reports',
        name: 'Reports',
        component: () => import('@/views/reports/ReportsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Reports' },
      },
      {
        path: 'resellers',
        name: 'Resellers',
        component: () => import('@/views/billing/ResellersView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Resellers' },
      },

      // ── Reseller Dashboard ────────────────────────────────────────────────────
      {
        path: 'reseller-dashboard',
        name: 'ResellerDashboard',
        component: () => import('@/views/reseller/ResellerDashboardView.vue'),
        meta: { requiresTenant: true, breadcrumb: 'Reseller Dashboard' },
      },
      {
        path: 'resellers/float',
        name: 'FloatManagement',
        component: () => import('@/views/reseller/FloatManagementView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Float Management' },
      },
      {
        path: 'reseller-reports',
        name: 'ResellerReports',
        component: () => import('@/views/reseller/ResellerReportsView.vue'),
        meta: { requiresTenant: true, roles: ['reseller'], breadcrumb: 'My Reports' },
      },
      {
        path: 'technician-dashboard',
        name: 'TechnicianDashboard',
        component: () => import('@/views/TechnicianDashboardView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'technician'], breadcrumb: 'Technician Dashboard' },
      },

      // ── PPPoE Subscriptions ───────────────────────────────────────────────────
      {
        path: 'pppoe',
        name: 'PppoeList',
        component: () => import('@/views/pppoe/PppoeListView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'reseller'], breadcrumb: 'PPPoE' },
      },
      {
        path: 'pppoe/:id',
        name: 'PppoeDetail',
        component: () => import('@/views/pppoe/PppoeDetailView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'reseller'], breadcrumb: 'Subscription' },
      },

      // ── Support Tickets ───────────────────────────────────────────────────────
      // Redirect /support/tickets → /support to prevent 'tickets' being matched as :id
      // Help — the manual for whoever is reading. Deliberately carries NO roles and no
      // requiresTenant: every role has a manual, and a person who cannot yet reach a screen
      // is exactly the person who needs to read about it. The optional :slug lets the
      // manuals cross-link to each other.
      {
        path: 'help/:slug?',
        name: 'Help',
        component: () => import('@/views/help/HelpView.vue'),
        meta: { breadcrumb: 'Help' },
      },
      { path: 'support/tickets', redirect: '/support' },
      {
        path: 'support',
        name: 'SupportTickets',
        component: () => import('@/views/support/SupportTicketsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'customer_support'], breadcrumb: 'Support' },
      },
      {
        path: 'support/analytics',
        name: 'TicketAnalytics',
        component: () => import('@/views/support/TicketAnalyticsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'customer_support'], breadcrumb: 'Ticket Analytics' },
      },
      {
        // Declared before support/:id so 'kb' is not swallowed by the :id param.
        path: 'support/kb',
        name: 'KnowledgeBase',
        component: () => import('@/views/support/KnowledgeBaseView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'customer_support'], breadcrumb: 'Knowledge Base' },
      },
      {
        path: 'support/:id',
        name: 'SupportTicketDetail',
        component: () => import('@/views/support/SupportTicketDetailView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'customer_support'], breadcrumb: 'Ticket' },
      },

      // ── Monitoring (NOC) ─────────────────────────────────────────────────────
      {
        path: 'monitoring',
        name: 'NocOverview',
        component: () => import('@/views/monitoring/NocOverviewView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'technician'], breadcrumb: 'Monitoring' },
      },
      {
        // Outage history + the review-and-apply compensation screen (ADR-057, C1.5b).
        // Technicians can see outages but not compensate — the route allows them in and
        // the API refuses the POST, which is the same split as the rest of monitoring.
        path: 'monitoring/outages',
        name: 'Outages',
        component: () => import('@/views/monitoring/OutagesView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'technician'], breadcrumb: 'Outages' },
      },
      {
        path: 'router-nodes',
        name: 'RouterNodes',
        component: () => import('@/views/network/RouterNodesView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'technician'], breadcrumb: 'Router Nodes' },
      },
      {
        path: 'network/topology',
        name: 'DeviceTopology',
        component: () => import('@/views/network/DeviceTopologyView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'technician'], breadcrumb: 'Device Topology' },
      },
      {
        path: 'fleet/ops',
        name: 'BulkFleetOps',
        component: () => import('@/views/network/BulkFleetOpsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'technician'], breadcrumb: 'Fleet Operations' },
      },

      // ── Network Ops (PND-N/O/P/R) ───────────────────────────────────────────
      {
        path: 'network/discovery',
        name: 'NetworkDiscovery',
        component: () => import('@/views/network/NetworkDiscoveryView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'technician'], breadcrumb: 'Network Discovery' },
      },
      {
        path: 'network/mac-bindings',
        name: 'MacBindings',
        component: () => import('@/views/network/MacBindingView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'technician'], breadcrumb: 'IP/MAC Bindings' },
      },
      {
        path: 'network/troubleshooting',
        name: 'Troubleshooting',
        component: () => import('@/views/network/TroubleshootingView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'technician'], breadcrumb: 'Troubleshooting' },
      },
      {
        path: 'devices/replace-router',
        name: 'ReplaceRouter',
        component: () => import('@/views/devices/ReplaceRouterView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'technician'], breadcrumb: 'Replace Router' },
      },

      // ── Subscribers (PND-T remaining) ─────────────────────────────────────
      {
        path: 'customers/fup',
        name: 'FupUsers',
        component: () => import('@/views/customers/FupUsersView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'FUP Users' },
      },
      {
        path: 'customers/roaming-partners',
        name: 'RoamingPartners',
        component: () => import('@/views/customers/RoamingPartnersView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Roaming Partners' },
      },
      {
        path: 'customers/roaming',
        name: 'RoamingUsers',
        component: () => import('@/views/customers/RoamingUsersView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Roaming Users' },
      },

      // ── Plans (PND-T remaining) ───────────────────────────────────────────
      {
        path: 'services/schedule-plans',
        name: 'SchedulePlans',
        component: () => import('@/views/services/SchedulePlansView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Schedule Plans' },
      },
      {
        path: 'services/tv-plans',
        name: 'TvPlans',
        component: () => import('@/views/services/TvPlansView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'TV Plans' },
      },
      {
        path: 'services/hotspot-trials',
        name: 'HotspotTrials',
        component: () => import('@/views/services/HotspotTrialsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Hotspot Trials' },
      },

      // ── Messaging (PND-Y) ─────────────────────────────────────────────────
      {
        path: 'messaging/whatsapp',
        name: 'WhatsApp',
        component: () => import('@/views/messaging/WhatsAppView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'WhatsApp' },
      },

      // ── Sessions & Logs ──────────────────────────────────────────────────────
      {
        path: 'sessions',
        name: 'Sessions',
        component: () => import('@/views/sessions/SessionListView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'technician'], breadcrumb: 'Sessions' },
      },
      {
        path: 'activation-logs',
        name: 'ActivationLogs',
        component: () => import('@/views/sessions/ActivationLogsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'technician'], breadcrumb: 'Activation Logs' },
      },
      {
        path: 'audit-logs',
        name: 'AuditLogs',
        component: () => import('@/views/sessions/AuditLogsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Audit Logs' },
      },

      // ── VPN Servers ──────────────────────────────────────────────────────────
      {
        path: 'vpn-servers',
        name: 'VpnServers',
        component: () => import('@/views/infra/VpnServerListView.vue'),
        meta: { roles: ['super_admin'], breadcrumb: 'VPN Servers' },
      },
      {
        path: 'vpn-servers/:serverId',
        name: 'VpnServerDetail',
        component: () => import('@/views/infra/VpnServerDetailView.vue'),
        meta: { roles: ['super_admin'], breadcrumb: 'VPN Server' },
      },
      {
        path: 'vpn-hub',
        name: 'VpnHub',
        component: () => import('@/views/infra/VpnHubView.vue'),
        meta: { roles: ['super_admin'], breadcrumb: 'VPN Hub' },
      },

      // ── SMS ───────────────────────────────────────────────────────────────────
      {
        path: 'sms',
        name: 'SmsList',
        component: () => import('@/views/sms/SmsListView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'SMS' },
      },
      {
        path: 'sms/compose',
        name: 'SmsCompose',
        component: () => import('@/views/sms/SmsComposeView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Compose SMS' },
      },
      {
        path: 'sms/bulk',
        name: 'SmsBulk',
        component: () => import('@/views/sms/SmsBulkView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Bulk SMS' },
      },
      {
        path: 'sms/templates',
        name: 'SmsTemplates',
        component: () => import('@/views/sms/SmsTemplatesView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'SMS Templates' },
      },
      {
        path: 'sms/pricing',
        name: 'SmsPricing',
        component: () => import('@/views/sms/SmsPricingView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'SMS Pricing' },
      },
      {
        path: 'sms/convert',
        name: 'SmsConvert',
        component: () => import('@/views/sms/SmsConvertView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'SMS Convert' },
      },
      {
        path: 'sms/provider',
        name: 'SmsProvider',
        component: () => import('@/views/sms/SmsProviderView.vue'),
        /**
         * Platform admins only.
         *
         * Peter, 2026-09-02: *"the set sms provider should not be allowed on my role, the
         * sms provider should be set by systems admin, this is another revenue stream on our
         * platform (selling SMS)."*
         *
         * A site owner who can point their own SMS provider at their own gateway account
         * stops buying SMS credits through JISPMAN — which is the product. Route-level is
         * only half the fix and the API gate matters more; this closes the visible half.
         */
        meta: { requiresTenant: true, roles: ['super_admin'], breadcrumb: 'SMS Provider' },
      },

      // ── Notifications ─────────────────────────────────────────────────────────
      {
        path: 'notifications/templates',
        name: 'NotificationTemplates',
        component: () => import('@/views/notifications/TemplatesView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Notification Templates' },
      },
      {
        path: 'notifications/channels',
        name: 'NotificationChannels',
        component: () => import('@/views/notifications/ChannelsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Notification Channels' },
      },
      {
        path: 'notifications/preferences',
        name: 'NotificationPreferences',
        component: () => import('@/views/notifications/PreferencesView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Notification Preferences' },
      },
      {
        path: 'notifications/alert-config',
        name: 'AlertConfig',
        component: () => import('@/views/notifications/AlertConfigView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Alert Config' },
      },
      {
        path: 'notifications/reminders',
        name: 'RenewalReminders',
        component: () => import('@/views/notifications/RemindersView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Reminders' },
      },
      {
        path: 'notifications/events',
        name: 'NotificationEvents',
        component: () => import('@/views/notifications/NotificationEventsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Notification Events' },
      },

      // ── Users & Roles ─────────────────────────────────────────────────────────
      {
        path: 'users',
        name: 'Users',
        component: () => import('@/views/users/UserListView.vue'),
        meta: { roles: ['super_admin', 'site_owner'], breadcrumb: 'Users' },
      },
      {
        path: 'users/new',
        name: 'UserCreate',
        component: () => import('@/views/users/UserEditView.vue'),
        meta: { roles: ['super_admin', 'site_owner'], breadcrumb: 'New User' },
      },
      {
        path: 'users/:userId/edit',
        name: 'UserEdit',
        component: () => import('@/views/users/UserEditView.vue'),
        meta: { roles: ['super_admin', 'site_owner'], breadcrumb: 'Edit User' },
      },

      // ── Security / Roles ─────────────────────────────────────────────────────
      {
        path: 'security/roles',
        name: 'SecurityRoles',
        component: () => import('@/views/security/RolesView.vue'),
        meta: { roles: ['super_admin'], breadcrumb: 'Roles' },
      },

      // Firmware artefacts are fleet-wide, so no requiresTenant: a technician
      // downloading an image is not acting on a particular site.
      {
        path: 'downloads',
        name: 'Downloads',
        component: () => import('@/views/downloads/DownloadsView.vue'),
        meta: { roles: ['super_admin'], breadcrumb: 'Downloads' },
      },

      // ── Settings ─────────────────────────────────────────────────────────────
      {
        path: 'settings/profile',
        name: 'Profile',
        component: () => import('@/views/settings/ProfileView.vue'),
        meta: { breadcrumb: 'Profile' },
      },
      {
        /**
         * Every user has one, whatever their role: a second factor is a property of a person,
         * not of a tenant. The login-alert email links straight here, so the path is part of a
         * contract with messages already in people's inboxes.
         */
        path: 'settings/security',
        name: 'SecuritySettings',
        component: () => import('@/views/settings/SecurityView.vue'),
        meta: { breadcrumb: 'Security' },
      },
      {
        path: 'settings/site',
        name: 'SiteSettings',
        component: () => import('@/views/settings/SiteSettingsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner'], breadcrumb: 'Site Settings' },
      },
      {
        path: 'settings/tax',
        name: 'TaxConfig',
        component: () => import('@/views/settings/TaxConfigView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner'], breadcrumb: 'Tax Configuration' },
      },
      {
        path: 'settings/global',
        name: 'GlobalSettings',
        component: () => import('@/views/settings/GlobalSettingsView.vue'),
        meta: { roles: ['super_admin'], breadcrumb: 'Global Settings' },
      },

      // ── New operator screens (redesign) ──────────────────────────────────────
      {
        path: 'pos',
        name: 'PosTerminal',
        component: () => import('@/views/pos/PosTerminalView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'reseller'], breadcrumb: 'POS Terminal' },
      },
      {
        path: 'leads',
        name: 'Leads',
        component: () => import('@/views/crm/LeadsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Leads' },
      },
      {
        path: 'invoices',
        name: 'Invoices',
        component: () => import('@/views/billing/InvoicesView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Invoices' },
      },
      {
        path: 'promos',
        name: 'PromoBanners',
        component: () => import('@/views/ads/PromoBannersView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Promo Banners' },
      },
      {
        path: 'content/static-pages',
        name: 'StaticPages',
        component: () => import('@/views/content/StaticPagesView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Static Pages' },
      },
      {
        path: 'content/static-pages/:slug',
        name: 'StaticPageEdit',
        component: () => import('@/views/content/StaticPageEditView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Edit Page' },
      },
      {
        path: 'payments/routing',
        name: 'GatewayRouting',
        component: () => import('@/views/billing/GatewayRoutingView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner'], breadcrumb: 'Gateway Routing' },
      },
      {
        path: 'payments/gateway-config',
        name: 'GatewayConfig',
        component: () => import('@/views/billing/GatewayConfigView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner'], breadcrumb: 'Gateway Config' },
      },
      {
        path: 'payments/failed',
        name: 'FailedPayments',
        component: () => import('@/views/billing/FailedPaymentsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Reconciliation' },
      },
      {
        path: 'inventory',
        name: 'Inventory',
        component: () => import('@/views/inventory/InventoryView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator', 'technician'], breadcrumb: 'Inventory' },
      },
      {
        path: 'system/api-keys',
        name: 'ApiKeys',
        component: () => import('@/views/system/ApiKeysView.vue'),
        meta: { roles: ['super_admin', 'site_owner'], breadcrumb: 'API Keys' },
      },
      {
        path: 'recycle-bin',
        name: 'RecycleBin',
        component: () => import('@/views/recycle/RecycleBinView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Recycle Bin' },
      },
      {
        path: 'settings/ai',
        name: 'AiSettings',
        component: () => import('@/views/settings/AiSettingsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner'], breadcrumb: 'AI Settings' },
      },
      {
        path: 'settings/alerts',
        name: 'OperatorAlerts',
        component: () => import('@/views/settings/OperatorAlertsView.vue'),
        meta: { requiresTenant: true, roles: ['super_admin', 'site_owner', 'operator'], breadcrumb: 'Operator Alerts' },
      },
      {
        path: 'system/health',
        name: 'SystemHealth',
        component: () => import('@/views/system/HealthView.vue'),
        meta: { roles: ['super_admin'], breadcrumb: 'System Health' },
      },
      {
        path: 'system/queues',
        name: 'SystemQueues',
        component: () => import('@/views/system/QueuesView.vue'),
        meta: { roles: ['super_admin'], breadcrumb: 'Queues' },
      },
      {
        path: 'system/backup',
        name: 'SystemBackup',
        component: () => import('@/views/system/BackupView.vue'),
        meta: { roles: ['super_admin'], breadcrumb: 'Backup' },
      },
    ],
  },

  // ── Platform Admin (super_admin only) ───────────────────────────────────────
  {
    path: '/admin',
    component: () => import('@/layouts/AdminLayout.vue'),
    meta: { requiresAuth: true, roles: ['super_admin'], breadcrumb: 'Platform' },
    children: [
      { path: '', name: 'PlatformOverview', component: () => import('@/views/admin/PlatformOverviewView.vue'), meta: { breadcrumb: 'Overview' } },
      { path: 'observability', name: 'Observability', component: () => import('@/views/admin/ObservabilityView.vue'), meta: { breadcrumb: 'Observability' } },
      { path: 'carriers', name: 'Carriers', component: () => import('@/views/admin/CarriersView.vue'), meta: { breadcrumb: 'Carriers' } },
      { path: 'tenants', name: 'Tenants', component: () => import('@/views/admin/TenantsView.vue'), meta: { breadcrumb: 'Tenants' } },
      { path: 'approvals', name: 'Approvals', component: () => import('@/views/admin/ApprovalsView.vue'), meta: { breadcrumb: 'Approvals' } },
      /**
       * Buying SMS credits and granting them to a site. The routes have existed for months
       * and nothing in the SPA called them, so adding credit meant hand-writing a request —
       * which is why the credit ledger had zero movement rows while the egosms account held
       * real money, and why sends failed "Insufficient SMS credits" against a funded gateway.
       */
      { path: 'sms-credits', name: 'SmsCredits', component: () => import('@/views/admin/SmsCreditsView.vue'), meta: { breadcrumb: 'SMS Credits' } },
      // Platform-wide by design: this queue is every site's advertiser submissions, and a
      // site owner clearing their own upload would not be review.
      { path: 'advertiser-vetting', name: 'AdvertiserVetting', component: () => import('@/views/admin/AdvertiserVettingView.vue'), meta: { breadcrumb: 'Advertiser Vetting' } },
      { path: 'finance', name: 'RevenueMargin', component: () => import('@/views/admin/RevenueMarginView.vue'), meta: { breadcrumb: 'Revenue & Margin' } },
      { path: 'invoices', name: 'AdminInvoices', component: () => import('@/views/admin/AdminInvoicesView.vue'), meta: { breadcrumb: 'Invoices' } },
      { path: 'ledger', name: 'CreditLedger', component: () => import('@/views/admin/CreditLedgerView.vue'), meta: { breadcrumb: 'Credit Ledger' } },
      { path: 'payments', name: 'AdminPayments', component: () => import('@/views/admin/AdminPaymentsView.vue'), meta: { breadcrumb: 'Payments' } },
      { path: 'pricing', name: 'Pricing', component: () => import('@/views/admin/PricingView.vue'), meta: { breadcrumb: 'Pricing' } },
      { path: 'audit', name: 'PlatformAudit', component: () => import('@/views/admin/AuditView.vue'), meta: { breadcrumb: 'Audit' } },
      { path: 'settings', name: 'PlatformSettings', component: () => import('@/views/admin/PlatformSettingsView.vue'), meta: { breadcrumb: 'Settings' } },
    ],
  },

  // ── 404 ───────────────────────────────────────────────────────────────────────
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundView.vue'),
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (_, __, saved) => saved ?? { top: 0 },
})

router.beforeEach(async to => {
  const auth = useAuthStore()
  const tenant = useTenantStore()

  // Initialize auth state on first navigation
  if (!auth.user && auth.accessToken) {
    await auth.init()
  }

  // Public routes — allow through
  if (to.meta.public) {
    if (auth.isAuthenticated) {
      const role = auth.user?.globalRole ?? (tenant.siteId && auth.user?.siteRoles?.[tenant.siteId])
      if (role === 'reseller') return { name: 'ResellerDashboard' }
      if (role === 'technician') return { name: 'TechnicianDashboard' }
      return { name: 'Dashboard' }
    }
    return true
  }

  // Auth gate
  if (to.meta.requiresAuth !== false && !auth.isAuthenticated) {
    return { name: 'Login', query: { redirect: to.fullPath } }
  }

  // Tenant gate — a site must be selected
  if (to.meta.requiresTenant && !tenant.siteId) {
    // Try to restore the last selected site from localStorage first (works for all roles)
    await tenant.init()
    // Multi-site users with no remembered site: auto-select their first site
    if (!tenant.siteId && !auth.isSuperAdmin && tenant.sites.length > 0) {
      await tenant.switchSite(tenant.sites[0].id)
    }
    // If still no site selected, redirect appropriately
    if (!tenant.siteId) {
      return auth.isSuperAdmin ? { name: 'Sites' } : { name: 'Dashboard' }
    }
  }

  // Role gate — check global role then per-site role from JWT siteRoles map
  const requiredRoles = to.meta.roles as string[] | undefined
  if (requiredRoles?.length) {
    const userGlobalRole = auth.user?.globalRole
    const userSiteRole = tenant.siteId ? auth.user?.siteRoles?.[tenant.siteId] : undefined

    const allowed =
      userGlobalRole === 'super_admin' ||
      requiredRoles.includes(userGlobalRole ?? '') ||
      (userSiteRole != null && requiredRoles.includes(userSiteRole))

    if (!allowed) {
      /**
       * SAY SO. This used to redirect in silence, and when the destination was the page the
       * user was already on — which it is for every blocked link on the dashboard — the
       * click did nothing at all. Nothing moved, nothing appeared, nothing failed. A person
       * clicks Devices three times and concludes the application is broken.
       *
       * Found by walking every route as each role on 2026-08-27.
       */
      const label = typeof to.meta.title === 'string' ? to.meta.title : String(to.name ?? to.path)
      useNotificationStore().warning(
        'Not available to your role',
        `You do not have access to ${label}. Ask your site owner if you need it.`,
      )
      const effectiveRole = userSiteRole ?? userGlobalRole
      if (effectiveRole === 'reseller') return { name: 'ResellerDashboard' }
      if (effectiveRole === 'technician') return { name: 'TechnicianDashboard' }
      return { name: 'Dashboard' }
    }
  }

  // Resellers never use the operator Dashboard — send them to their own
  if (to.name === 'Dashboard' && !auth.user?.globalRole) {
    const siteRole = tenant.siteId ? auth.user?.siteRoles?.[tenant.siteId] : undefined
    const allSiteRoles = Object.values(auth.user?.siteRoles ?? {})
    if (siteRole === 'reseller' || (siteRole == null && allSiteRoles.length > 0 && allSiteRoles.every(r => r === 'reseller'))) {
      return { name: 'ResellerDashboard' }
    }
  }

  // ── Setup gate (ADR-084) ────────────────────────────────────────────────────
  //
  // Holds a brand-new, SELF-REGISTERED tenant on the setup path until they can actually
  // trade, so they meet the product in an order that works instead of wandering into
  // Vouchers before they have a package and concluding it is broken.
  //
  // Everything about this gate is built to fail in the harmless direction, because the two
  // failure modes are not remotely symmetric: wrongly letting somebody through costs them an
  // empty page, and wrongly keeping somebody out costs them their business for the afternoon.
  // So the exits come first and the gate comes last.
  if (await isSetupGated(to)) {
    useNotificationStore().info(
      'Finish setting up first',
      'A few essentials are still missing. They are in order on this page — the rest of the '
      + 'app opens up as soon as they are done.',
    )
    return { name: 'Setup' }
  }

  return true
})

/**
 * Routes that stay reachable while a tenant is gated.
 *
 * An allowlist by NAME, because the alternative — `setupExempt: true` on each of 150 route
 * records — is a list that silently rots: a route added without the flag is a route a gated
 * tenant cannot reach, and nobody discovers it until a new customer complains.
 *
 * What is on it, and why:
 *  * `Setup` — the destination. Omitting it redirects the route to itself, which vue-router
 *    reports as an infinite redirect and the user sees as a blank screen.
 *  * The three dashboards — somewhere to land. `ResellerDashboard` and `TechnicianDashboard`
 *    specifically because the role branches above send those roles there, and a gate that
 *    bounced them back would be a loop between two guards rather than one.
 *  * Settings / Profile / Sites / Locations — a new owner must be able to finish their own
 *    business details, and change their password, while gated.
 *  * Support and the knowledge base — the screens somebody stuck on this page needs most.
 */
const SETUP_ALLOWLIST = new Set([
  'Setup',
  'Dashboard', 'ResellerDashboard', 'TechnicianDashboard',
  'Profile', 'SiteSettings', 'GlobalSettings', 'NotificationPreferences',
  'Sites', 'SiteDetail', 'SiteEdit', 'SiteCreate',
  'Locations', 'LocationDetail', 'LocationCreate',
  'SupportTickets', 'SupportTicketDetail', 'KnowledgeBase', 'Help',
])

/**
 * Fail loudly in development if a name above does not exist.
 *
 * An allowlist entry that matches no route is invisible: the Set accepts any string, so a
 * typo'd or renamed name simply stops exempting the page it was meant to protect, and the
 * only symptom is that a gated tenant cannot reach their own settings. The first draft of
 * this list had FIVE such names — `Settings`, `SiteNew`, `LocationNew`, `Notifications` and
 * `Support` — none of which are what the routes are actually called.
 */
if (import.meta.env.DEV) {
  const known = new Set<string>()
  const walk = (rs: readonly RouteRecordRaw[]): void => {
    for (const r of rs) {
      if (r.name) known.add(String(r.name))
      if (r.children) walk(r.children)
    }
  }
  walk(routes)
  const missing = [...SETUP_ALLOWLIST].filter(n => !known.has(n))
  if (missing.length) {
    console.error(
      '[setup-gate] SETUP_ALLOWLIST names that match no route — a gated tenant cannot reach '
      + 'these pages and nothing else will tell you:', missing,
    )
  }
}

async function isSetupGated(to: RouteLocationNormalized): Promise<boolean> {
  const auth = useAuthStore()
  const tenant = useTenantStore()

  // A platform admin is never gated. They are not setting up a business; they are looking at
  // somebody else's, and a super_admin who switched into a new tenant to HELP them finish
  // setup must not be locked out of the tools for doing it.
  if (auth.isSuperAdmin) return false

  // No tenant pinned yet — nothing to be ready or unready about.
  if (!tenant.siteId) return false

  if (to.meta.setupExempt === true) return false
  if (to.name && SETUP_ALLOWLIST.has(String(to.name))) return false

  const setup = useSetupStore()
  try {
    await setup.fetch(tenant.siteId)
  } catch {
    // Fail open: never let a readiness lookup be the reason somebody cannot work.
    return false
  }

  return setup.gated
}
